/**
 * 제출 전 확인(상태표 7-1). 안내용이며 최종 판정은 서버 응답이다.
 * 서버는 저장된 값으로 판정하므로, 화면 값이 저장되지 않은 상태에서는 제출을 보내지 않는다.
 */
import { classifyOmrInput } from '@growing/domain-rules';
import type { DisplayStatus } from './save-manager.js';
import { isEmptyValue, type ItemView, type TargetValue } from './types.js';

export interface PrecheckInput {
  readonly items: readonly ItemView[];
  readonly allowUnanswered: boolean;
  readonly status: (id: string) => DisplayStatus;
  readonly value: (id: string) => TargetValue;
}

export type PrecheckResult =
  | { readonly kind: 'wait' }
  | {
      readonly kind: 'blocked';
      readonly unsaved: readonly string[];
      readonly unselectedPoints: readonly string[];
      readonly inputErrors: readonly string[];
      readonly unanswered: readonly string[];
    }
  | { readonly kind: 'ok'; readonly unansweredCount: number };

/** 순서: 화면 문항 순서, GR은 segments의 지점 출현 순서 */
export function targetOrder(items: readonly ItemView[]): { id: string; kind: 'gr' | 'choice'; itemId: string }[] {
  const out: { id: string; kind: 'gr' | 'choice'; itemId: string }[] = [];
  for (const it of [...items].sort((a, b) => a.order - b.order)) {
    if (it.responseMode === 'single-choice') out.push({ id: it.id, kind: 'choice', itemId: it.id });
    else for (const s of it.segments ?? []) if (s.kind === 'point') out.push({ id: s.pointId, kind: 'gr', itemId: it.id });
  }
  return out;
}

export function precheck(p: PrecheckInput): PrecheckResult {
  const order = targetOrder(p.items);
  if (order.some((t) => p.status(t.id) === 'dirty' || p.status(t.id) === 'saving')) return { kind: 'wait' };
  const unsaved = order.filter((t) => p.status(t.id) === 'failed' || p.status(t.id) === 'conflict').map((t) => t.id);
  const unselectedPoints = order.filter((t) => t.kind === 'gr' && p.value(t.id) === null).map((t) => t.id);
  const optionCount = new Map(p.items.map((it) => [it.id, it.options.length]));
  // OMR 값은 서버와 같은 도메인 규칙(classifyOmrInput)으로 분류한다. 최종 판정은 서버.
  const omrState = (id: string, raw: string) => classifyOmrInput(raw, Math.max(1, optionCount.get(id) ?? 1)).state;
  const inputErrors = order.filter((t) => {
    const v = p.value(t.id);
    if (t.kind !== 'choice' || v === null || typeof v !== 'object') return false;
    if (v.kind === 'omr') return omrState(t.id, v.raw) === 'input-error';
    return v.kind === 'other' && v.state === 'input-error';
  }).map((t) => t.id);
  const unansweredAll = order.filter((t) => {
    if (t.kind !== 'choice') return false;
    const v = p.value(t.id);
    if (v !== null && typeof v === 'object' && v.kind === 'omr') return omrState(t.id, v.raw) === 'unanswered';
    return isEmptyValue(v);
  }).map((t) => t.id);
  const unanswered = p.allowUnanswered ? [] : unansweredAll;
  if (unsaved.length + unselectedPoints.length + inputErrors.length + unanswered.length > 0) {
    return { kind: 'blocked', unsaved, unselectedPoints, inputErrors, unanswered };
  }
  return { kind: 'ok', unansweredCount: unansweredAll.length };
}
